import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Modal, Space, Typography } from "antd";
import { resyncMerchantInsights } from "../insightsAdminApi";
import ForecastModelStatus from "./ForecastModelStatus";

/**
 * Rebuilds one merchant's insights now rather than at 07:00, then retrains.
 *
 * Confirmed before firing: it queues a 90-day rebuild, a scoped dailyAggregates
 * pass and a fleet-wide training job — not a button to lean on.
 */
interface Props {
  businessId?: string;
  businessName?: string;
}

function queuedKey(businessId: string): string {
  return `insights-rebuild-queued:${businessId}`;
}

function readQueuedAt(businessId?: string): string | undefined {
  if (!businessId) return undefined;
  try {
    return sessionStorage.getItem(queuedKey(businessId)) ?? undefined;
  } catch {
    return undefined;
  }
}

function writeQueuedAt(businessId: string, queuedAt: string): void {
  try {
    sessionStorage.setItem(queuedKey(businessId), queuedAt);
  } catch {
    // Private mode / quota — in-memory state still covers this tab.
  }
}

function clearQueuedAt(businessId?: string): void {
  if (!businessId) return;
  try {
    sessionStorage.removeItem(queuedKey(businessId));
  } catch {
    // ignore
  }
}

export default function InsightsResyncButton({
  businessId,
  businessName,
}: Props) {
  const [isSaving, setIsSaving] = useState(false);
  const [done, setDone] = useState<string>();
  const [rebuildQueuedAt, setRebuildQueuedAt] = useState<string>();
  // Bumped on a successful queue so the status panel re-reads immediately.
  const [refreshToken, setRefreshToken] = useState(0);
  const [error, setError] = useState<string>();

  useEffect(() => {
    setRebuildQueuedAt(readQueuedAt(businessId));
    setDone(undefined);
    setError(undefined);
  }, [businessId]);

  const settleRebuild = useCallback(() => {
    clearQueuedAt(businessId);
    setRebuildQueuedAt(undefined);
    setDone("Rebuild finished — the numbers below are the new model.");
  }, [businessId]);

  const run = async (): Promise<void> => {
    if (!businessId) return;

    setIsSaving(true);
    setError(undefined);
    setDone(undefined);

    try {
      const result = await resyncMerchantInsights(businessId);
      const queuedAt = new Date().toISOString();

      writeQueuedAt(businessId, queuedAt);
      setRebuildQueuedAt(queuedAt);
      setDone(
        `Rebuilding ${result.lookbackDays} days, then retraining the model. ` +
          "The status below stays on Retraining until the new model lands." +
          (result.dailyAggregatesRequested
            ? " Daily aggregates queued too."
            : "")
      );
      setRefreshToken((n) => n + 1);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          "Could not start the rebuild"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const confirm = (): void => {
    Modal.confirm({
      title: "Rebuild insights and retrain?",
      okText: "Rebuild",
      content: (
        <Space direction="vertical" size={4}>
          <Typography.Text>
            Rebuilds the last 90 days for{" "}
            <strong>{businessName ?? "this merchant"}</strong>.
          </Typography.Text>
          <Typography.Text type="secondary">
            Rebuilds the figures, then retrains the forecast model on them. Runs
            in the background and takes a few minutes. A longer seed is
            truncated to that window.
          </Typography.Text>
        </Space>
      ),
      onOk: run,
    });
  };

  const isRebuilding = Boolean(rebuildQueuedAt);

  return (
    <Space direction="vertical" size={6} style={{ width: "100%" }}>
      <Space>
        <Button
          onClick={confirm}
          loading={isSaving || isRebuilding}
          disabled={!businessId || isRebuilding}
        >
          {isRebuilding ? "Rebuilding insights…" : "Rebuild insights & retrain"}
        </Button>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Use after changing the end-of-day hour or seeding transactions.
        </Typography.Text>
      </Space>
      {done && !isRebuilding && (
        <Alert type="success" showIcon message={done} />
      )}
      {error && <Alert type="error" showIcon message={error} />}

      <ForecastModelStatus
        businessId={businessId}
        refreshToken={refreshToken}
        rebuildQueuedAt={rebuildQueuedAt}
        onRebuildSettled={settleRebuild}
      />
    </Space>
  );
}
